<script>
   /****************************************************************/
   /* PlotClassStat                                                */
   /* --------------------                                         */
   /* shows plot with classification statistics vs number of PCs   */
   /****************************************************************/

   import { range, mrange } from 'mdatools/stat';
   import { Segments, Legend, Rectangles } from 'svelte-plots-basic/2d';

   import { csvRow, downloadCSV } from '../utils.js';
   import { capt, LINE_COLORS, FACE_COLORS, MARKERS } from './Defaults.js';
   import PlotSeries from './PlotSeries.svelte';
   import Axes from './Axes.svelte';

   let {
      params,                        // JSON with main plot parameters
      props,                         // JSON with main properties (model, results, dataset)
   } = $props()

   const statMap = {
      'sens': 'sensitivity',
      'sel': 'selectivity',
      'spec': 'specificity',
      'eff': 'efficiency'
   };

   // file name for the plot
   const statName = $derived(statMap[params.statName]);
   const fileName = $derived('plot-' + statName);

   // get the properties needed for the plot (note we use classres here not res)
   const res = $derived(props && props.classres ? props.classres : null);
   const ncomp = $derived(props && props.ncomp ? props.ncomp : null)
   const resIDs = $derived(params.resID !== 'both' ? [params.resID] : Object.keys(res));
   const legendPosition = $derived(params.legendPosition ? params.legendPosition : 'bottomright');

   // compute max number of components and collect legend items
   const legendItems = $derived(resIDs ? resIDs.map(v => ({
      id: v,
      label: v,
      point: {
         marker: 1,
         lineColor: LINE_COLORS[v],
         faceColor: FACE_COLORS[v],
         marker: MARKERS[v],
         markerSize: 0.85,
         lineWidth: 1.5
      }
   })) : null);

   // compute ci
   let ci = $derived.by(() => {
      if (!params.ci || !params.ci[params.statName]) return null;
      const ci = params.ci[params.statName];
      if (!Array.isArray(ci) || ci.length !== 2) return null;
      return ci;
   });


   // compute limits
   const nc = $derived(res ? res[resIDs[0]].stat.components.length : 1);
   const dX = $derived(nc > 10 ? nc * 0.05 : 0.5)
   const xMin = $derived(nc > 1 ? 1 : 0);
   const xMax = $derived(nc > 1 ? nc : 2);
   const limX = $derived([xMin - dX, xMax + dX]);


   let limY = $derived.by(() => {
      if (!params.zoom) return [0, 1.1];
      if (!resIDs || resIDs.length < 1) return [0, 1];
      const v = resIDs.map(id => range(res[id].stat[statName]));
      return mrange(v.flat(), 0.2);
   });

   // method for creating CSV version of the plot data
   function downloadCSVCallback() {

      // 'props.data' is a map keyed by result id: the first set's separator
      const { sep, dec } = Object.values(props.data)[0];

      let x = [];
      let y = [];
      let s = [];

      for (let id of resIDs) {
         const n = res[id].stat.components.length;
         x = [...x, ...res[id].stat.components];
         y = [...y, ...res[id].stat[statName]];
         s = [...s, ...Array(n).fill(id)];
      }

      let out = csvRow(['Set', 'Components', capt(statName)], sep, dec);
      for (let i = 0; i < x.length; i++) {
         out += csvRow([s[i], x[i], y[i]], sep, dec);
      }

      downloadCSV(out, fileName);
   }
</script>

{#if res }

<Axes title={capt(statName)} xLabel='Components' yLabel={statName} {limX} {limY} {fileName} {downloadCSVCallback} whole={true}>

   <!-- vertical line with selected component -->
   {#if ncomp}
   <Segments xStart={[ncomp]} xEnd={[ncomp]} yStart={[limY[0]]} yEnd={[limY[1]]} lineColor='#a0a0a0' />
   {/if}

   <!-- confidence interval -->
   {#if ci}
   {@const dX = limX[1] - limX[0]}
   <Rectangles left={[limX[0]]} top={[ci[0] + ci[1]]} height={[2 * ci[1]]} width={[dX]} lineColor='#transparent' faceColor='#00000010'/>
   <Segments xStart={[limX[0]]} xEnd={[dX]} yStart={[ci[0]]} yEnd={[ci[0]]} lineColor='#a0a0a0' lineType={2}/>
   {/if}

   <!-- data points with statistics -->
   {#each resIDs as id, i}
      <PlotSeries
         xValues={res[id].stat.components}
         yValues={res[id].stat[statName]}
         plotType='b'
         showLabels={false}
         faceColor={FACE_COLORS[id]}
         mainColor={LINE_COLORS[id]}
         marker={MARKERS[id]}
         lineWidth={1.5}
      />
   {/each}

   <!-- points for selected component -->
   {#if ncomp}
   {#each resIDs as id}
      <PlotSeries
         xValues={[res[id].stat.components[ncomp - 1]]}
         yValues={[res[id].stat[statName][ncomp - 1]]}
         plotType='b'
         showLabels={false}
         faceColor={LINE_COLORS[id]}
         mainColor={LINE_COLORS[id]}
         marker={MARKERS[id]}
         markerSize={1}
         lineWidth={1}
      />
   {/each}
   {/if}


   {#if legendItems && legendItems.length > 0}
   <Legend items={legendItems} position={legendPosition} />
   {/if}
</Axes>
{/if}
